import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Paper,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Home as HomeIcon,
  AccountBalanceWallet as BudgetIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Pets as PetsIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useGameState } from "../providers/GameStateProvider";
import { useSupabase } from "../providers/SupabaseProvider";
import { supabase } from "../providers/SupabaseProvider";
import { BOTTOM_NAVIGATION_HEIGHT } from "../game/BackgroundImage";

interface PageProps {
  title: string;
  children: React.ReactNode;
  hideHeader?: boolean;
}

export default function Page({
  title,
  children,
  hideHeader = false,
}: PageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { adultMode, setAdultMode } = useGameState();
  const { user } = useSupabase();

  const handleAdultModeToggle = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newMode = event.target.checked;
    setAdultMode(newMode);

    // Update user metadata in Supabase
    if (user) {
      const { error } = await supabase.auth.updateUser({
        data: { adultMode: newMode },
      });
      if (error) {
        console.error("Failed to update adult mode:", error);
      }
    }
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Top Bar */}
      {!hideHeader && (
        <AppBar position="static" elevation={0}>
          <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h6">{title}</Typography>
            {user && ( // Only show toggle if user is logged in
              <FormControlLabel
                control={
                  <Switch
                    checked={adultMode}
                    onChange={handleAdultModeToggle}
                    color="secondary"
                  />
                }
                label={adultMode ? "Adult Mode" : "Kid Mode"}
                sx={{ color: "white" }}
              />
            )}
          </Toolbar>
        </AppBar>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: "auto", position: "relative" }}>
        {children}
      </Box>

      {/* Bottom Navigation */}
      <Paper
        sx={{ position: "static", height: BOTTOM_NAVIGATION_HEIGHT }}
        elevation={3}
        style={{ display: "none" }} // TODO: re-enable this
      >
        <BottomNavigation
          value={location.pathname}
          onChange={(_, newValue) => {
            navigate(newValue);
          }}
          showLabels
        >
          <BottomNavigationAction label="Home" value="/" icon={<HomeIcon />} />
          <BottomNavigationAction
            label="Budget"
            value="/budget"
            icon={<BudgetIcon />}
          />
          <BottomNavigationAction
            label="Showcase"
            value="/showcase"
            icon={<PetsIcon />}
          />
          <BottomNavigationAction
            label="History"
            value="/history"
            icon={<HistoryIcon />}
          />
          <BottomNavigationAction
            label="Settings"
            value="/settings"
            icon={<SettingsIcon />}
          />
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
